import { useMemo, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Repeat2 } from "lucide-react";
import { SortingState, VisibilityState } from "@tanstack/react-table";
import { cn } from "@/lib/utils";
import type {
  Asset,
  CurrentValuationSnapshot,
  PerformanceResult,
} from "@/schemas";
import { AssetService } from "@/services/asset-service";
import { getColumns, AssetTablePerformance } from "../columns";
import { DataTable } from "../data-table";
import { CashTable, getCashPositions } from "../CashTable";
import { AssetTradeModal } from "../AssetTradeModal";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobilePositionsList } from "./MobilePositionsList";
import { PositionsColumnsSettingsModal } from "./PositionsColumnsSettingsModal";
import { mergeCurrentValuationIntoPerformance } from "../../utils/current-valuation-utils";
import { usePerformanceQuery } from "../../hooks/queries";
import { usePortfolioPositionsTablePreferences } from "../../hooks/usePortfolioPositionsTablePreferences";
import {
  PORTFOLIO_POSITIONS_ALWAYS_VISIBLE,
  PORTFOLIO_POSITIONS_DEFAULT_ORDER,
  PORTFOLIO_POSITIONS_DEFAULT_VISIBILITY,
  mergeVisibilityWithDefaults,
  normalizePortfolioPositionsOrder,
} from "../../utils/positions-table-preferences";

interface PositionsTableProps {
  performanceData: PerformanceResult | null;
  performance1d: PerformanceResult | null;
  selectedBookId: string;
  currencyCode: string;
  loading: boolean;
}

type PositionFilter = "open" | "closed" | "all";
type PnlDisplayMode = "absolute" | "percentage";

export function PositionsTable({
  performanceData,
  performance1d,
  selectedBookId,
  currencyCode,
  loading,
}: PositionsTableProps) {
  const [positionFilter, setPositionFilter] = useState<PositionFilter>("open");
  const [pnlDisplayMode, setPnlDisplayMode] =
    useState<PnlDisplayMode>("absolute");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnsModalOpen, setColumnsModalOpen] = useState(false);
  const [tradeModalOpen, setTradeModalOpen] = useState(false);
  const [selectedAssetForModal, setSelectedAssetForModal] =
    useState<AssetTablePerformance | null>(null);
  const [assetsById, setAssetsById] = useState<Record<string, Asset>>({});
  const isMobile = useIsMobile();

  const prefsQuery = usePortfolioPositionsTablePreferences();

  const layoutFromServer = useMemo(() => {
    const pref = prefsQuery.data;
    if (!pref) {
      return {
        columnOrder: [...PORTFOLIO_POSITIONS_DEFAULT_ORDER],
        visibility: mergeVisibilityWithDefaults(undefined),
      };
    }
    return {
      columnOrder: normalizePortfolioPositionsOrder(pref.column_order ?? []),
      visibility: mergeVisibilityWithDefaults(pref.visibility ?? {}),
    };
  }, [prefsQuery.data]);

  const columnOrder = layoutFromServer.columnOrder;

  const columnVisibility: VisibilityState = useMemo(() => {
    const v: VisibilityState = { ...layoutFromServer.visibility };
    PORTFOLIO_POSITIONS_ALWAYS_VISIBLE.forEach((id) => {
      v[id] = true;
    });
    return v;
  }, [layoutFromServer.visibility]);

  const { positionsData, cashData } = useMemo(() => {
    if (!performanceData?.data?.assets_performance)
      return { positionsData: [], cashData: [] };

    const perf1dMap = performance1d?.data?.assets_performance ?? null;
    const totalBookValue =
      performanceData.data.total_performance?.total_current_value ?? 0;

    const all = Object.entries(performanceData.data.assets_performance).map(
      ([assetIdKey, assetPerf]) => {
        const assetId = assetIdKey || assetPerf.asset_symbol;

        const baseAsset = {
          asset_id: assetId,
          name: assetPerf.asset_name || assetPerf.asset_symbol,
          symbol: assetPerf.asset_symbol,
          type: assetPerf.asset_type as
            | "stock"
            | "crypto"
            | "fiat"
            | "index"
            | "etf"
            | "fund",
          last_sync: new Date().toISOString(),
        };

        let asset: Asset;
        if (
          assetPerf.asset_type === "stock" ||
          assetPerf.asset_type === "etf" ||
          assetPerf.asset_type === "fund"
        ) {
          asset = {
            ...baseAsset,
            type: assetPerf.asset_type as "stock" | "etf" | "fund",
            exchange: "",
            isin: "",
            currency: assetPerf.asset_currency || "USD",
            website: assetPerf.asset_website || undefined,
          } as Asset;
        } else if (assetPerf.asset_type === "crypto") {
          asset = {
            ...baseAsset,
            type: "crypto",
            currency: assetPerf.asset_currency || "USD",
          } as Asset;
        } else if (assetPerf.asset_type === "index") {
          asset = {
            ...baseAsset,
            type: "index",
            provider: "",
            components: [],
          } as Asset;
        } else {
          asset = {
            ...baseAsset,
            type: "fiat",
          } as Asset;
        }

        const p1d = perf1dMap?.[assetId];
        const dailyAbs = p1d?.performance_absolute;
        const dailyPct = p1d?.performance_percentage;
        const weightPct =
          totalBookValue > 0 && !assetPerf.is_closed
            ? (assetPerf.current_value / totalBookValue) * 100
            : undefined;

        return {
          ...asset,
          ...assetPerf,
          daily_pnl_base: dailyAbs,
          daily_return_pct: dailyPct,
          weight_pct: weightPct,
        };
      },
    );

    const cash = getCashPositions(performanceData, currencyCode);
    const positions = all
      .filter((a) => a.type !== "fiat")
      .filter((asset) => {
        if (positionFilter === "open") return !asset.is_closed;
        if (positionFilter === "closed") return asset.is_closed;
        return true;
      });

    return { positionsData: positions, cashData: cash };
  }, [currencyCode, performanceData, performance1d, positionFilter]);

  const positionAssetIds = useMemo(() => {
    const ids = positionsData.map((asset) => asset.asset_id).filter(Boolean);
    return Array.from(new Set(ids));
  }, [positionsData]);

  useEffect(() => {
    let cancelled = false;
    const fetchAssets = async () => {
      if (positionAssetIds.length === 0) {
        setAssetsById({});
        return;
      }

      try {
        const assets = await AssetService.getAssetsByIDs(positionAssetIds);
        if (cancelled) return;
        const map: Record<string, Asset> = {};
        assets.forEach((asset) => {
          map[asset.asset_id] = asset;
        });
        setAssetsById(map);
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load asset details", error);
        }
      }
    };

    void fetchAssets();
    return () => {
      cancelled = true;
    };
  }, [positionAssetIds]);

  const enrichedPositionsData = useMemo(() => {
    return positionsData.map((row) => {
      const fullAsset = assetsById[row.asset_id];
      const merged = fullAsset ? { ...row, ...fullAsset } : { ...row };
      const quotePct =
        merged.daily_change_percent !== undefined
          ? merged.daily_change_percent
          : undefined;
      return {
        ...merged,
        asset_quote_daily_change_pct: quotePct,
      } as AssetTablePerformance;
    });
  }, [positionsData, assetsById]);

  const columns = useMemo(
    () =>
      getColumns({
        currencyCode,
        onViewTrades: (asset) => {
          setSelectedAssetForModal(asset);
          setTradeModalOpen(true);
        },
        isMobile,
        optionalColumnsPicker: true,
      }),
    [currencyCode, isMobile],
  );

  const positionFilterControl = (
    <div className="flex items-center bg-muted/50 rounded-lg p-0.5 border border-border/40">
      {(["open", "closed", "all"] as const).map((f) => (
        <button
          key={f}
          type="button"
          onClick={() => setPositionFilter(f)}
          className={cn(
            "px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md transition-all",
            positionFilter === f
              ? "bg-background text-primary shadow-sm"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {f}
        </button>
      ))}
    </div>
  );

  const visibilityForModal = useMemo(() => {
    const v: Record<string, boolean> = { ...PORTFOLIO_POSITIONS_DEFAULT_VISIBILITY };
    for (const [k, val] of Object.entries(layoutFromServer.visibility)) {
      if (PORTFOLIO_POSITIONS_ALWAYS_VISIBLE.has(k)) continue;
      v[k] = !!val;
    }
    return v;
  }, [layoutFromServer.visibility]);

  return (
    <>
      <div className="flex-1 min-h-0 flex flex-col bg-card rounded-2xl border border-border/40 shadow-sm overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-border/40 flex-shrink-0 bg-secondary/10 gap-3">
          <div className="flex min-w-0 items-center justify-between gap-3 sm:block">
            <div className="flex items-center justify-between gap-3 sm:justify-start sm:gap-4">
              <span className="text-xl font-bold text-foreground">Positions</span>

              {isMobile ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2.5 text-xs text-muted-foreground"
                  onClick={() =>
                    setPnlDisplayMode((prev) =>
                      prev === "absolute" ? "percentage" : "absolute",
                    )
                  }
                >
                  <Repeat2 className="mr-1.5 h-3.5 w-3.5" />
                  {pnlDisplayMode === "absolute" ? "Net P&L" : "Net %"}
                </Button>
              ) : (
                positionFilterControl
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 flex-wrap sm:shrink-0">
            {isMobile && positionFilterControl}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="w-full">
            {loading || prefsQuery.isLoading ? (
              <div className="p-5">
                <Skeleton className="h-40 w-full" />
              </div>
            ) : positionsData.length > 0 || cashData.length > 0 ? (
              <div className="flex flex-col">
                {positionsData.length > 0 &&
                  (isMobile ? (
                    <MobilePositionsList
                      data={enrichedPositionsData}
                      currencyCode={currencyCode}
                      pnlDisplayMode={pnlDisplayMode}
                      onViewTrades={(asset) => {
                        setSelectedAssetForModal(asset);
                        setTradeModalOpen(true);
                      }}
                    />
                  ) : (
                    <DataTable
                      columns={columns}
                      sorting={sorting}
                      onSortingChange={setSorting}
                      data={enrichedPositionsData}
                      pagination={false}
                      columnVisibility={columnVisibility}
                      onColumnVisibilityChange={() => {}}
                      columnOrder={columnOrder}
                      onCustomizeColumns={() => setColumnsModalOpen(true)}
                      showColumnPicker={false}
                      tableClassName="min-w-[720px] md:min-w-full"
                    />
                  ))}

                {cashData.length > 0 && (
                  <CashTable
                    cashPositions={cashData}
                    baseCurrencyCode={currencyCode}
                    className="mt-4"
                  />
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground text-sm py-12">
                No positions in this portfolio
              </div>
            )}
          </div>
        </div>
      </div>

      {!isMobile && (
        <PositionsColumnsSettingsModal
          open={columnsModalOpen}
          onOpenChange={setColumnsModalOpen}
          columnOrder={columnOrder}
          visibility={visibilityForModal}
        />
      )}

      <AssetTradeModal
        asset={selectedAssetForModal}
        bookId={selectedBookId}
        open={tradeModalOpen}
        onOpenChange={setTradeModalOpen}
      />
    </>
  );
}

export function PositionsTableContainer({
  currentPerformance,
  currentValuation,
  selectedBookId,
  currencyCode,
  loadingBase,
}: {
  currentPerformance: PerformanceResult | null;
  currentValuation: CurrentValuationSnapshot | null;
  selectedBookId: string;
  currencyCode: string;
  loadingBase: boolean;
}) {
  const performanceQuery1d = usePerformanceQuery(selectedBookId, "1d");

  const mergedCurrent = useMemo(
    () =>
      mergeCurrentValuationIntoPerformance(
        currentPerformance,
        currentValuation,
      ),
    [currentPerformance, currentValuation],
  );

  const merged1d = useMemo(
    () =>
      mergeCurrentValuationIntoPerformance(
        (performanceQuery1d.data as PerformanceResult | null) ?? null,
        currentValuation,
      ),
    [performanceQuery1d.data, currentValuation],
  );

  const loading = loadingBase || performanceQuery1d.isLoading;

  return (
    <PositionsTable
      performanceData={mergedCurrent}
      performance1d={merged1d}
      selectedBookId={selectedBookId}
      currencyCode={currencyCode}
      loading={loading}
    />
  );
}
